import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import "../styles/CareerStatsModern.css";
import ui_id_img from "../assets/ui_id.png";
import url_id_img from "../assets/url_id.png";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Plot from "react-plotly.js"; // Import Plotly
import CategoryIcon from "./CategoryIcon";
import { maskDriverName, authHeaders } from "../utils/driverPrivacy";
import HomeProfileCard from "./HomeProfileCard";
import { CHART, chartAxis, chartBase, chartWatermark } from "../utils/chartTheme";

const ALL_CATEGORIES = ["sports_car", "formula_car", "oval", "dirt_road", "dirt_oval", "road"];

const CATEGORY_LABELS = {
        sports_car: "Sports car",
        formula_car: "Formula car",
        oval: "Oval",
        dirt_road: "Dirt road",
        dirt_oval: "Dirt oval",
        road: "Road",
};

// One color per category, shared by the iRating tiles, chart lines and toggles.
const CATEGORY_COLORS = {
        sports_car: "#f1c40f",
        formula_car: "#4c8dff",
        oval: "#ef5b5b",
        dirt_road: "#b583f2",
        dirt_oval: "#d9944f",
        road: "#9aa0a8",
};

// Rarity tiers for percentile badges, by percentile (100 - top %).
const percentileTier = (percentile) => {
        if (percentile <= 49) return "base";
        if (percentile <= 74) return "good";
        if (percentile <= 89) return "great";
        if (percentile <= 99) return "elite";
        return "top";
};

const formatStat = (value) => (typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value);

function StatBox({ title, value, percentileData }) {
        const top = percentileData?.rank?.topPercent;
        const percentileLabel = Number.isFinite(top)
                ? top < 1
                        ? "Top <1%"
                        : top > 99
                          ? "Top >99%"
                          : `Top ${top.toFixed(0)}%`
                : value === "N/A" || percentileData?.version === 2
                  ? "N/A"
                  : "Pending refresh";
        const tier = Number.isFinite(top) ? percentileTier(100 - top) : "none";
        const tooltip =
                percentileData?.version === 2
                        ? `${percentileData.population}. Sample: ${percentileData.sampleCount?.toLocaleString()} drivers. Updated: ${new Date(percentileData.last_update).toLocaleString()}. Ties use their midpoint rank.`
                        : "Percentile refresh pending";

        return (
                <div className="cs-stat">
                        <span className="cs-stat__label">{title}</span>
                        <span className="cs-stat__value">{formatStat(value)}</span>
                        <span className={`cs-pct cs-pct--${tier}`} title={tooltip}>
                                {percentileLabel}
                        </span>
                </div>
        );
}

// Wrapper for the loading, queued, private and error states.
const StatePanel = ({ title, busy, children }) => (
        <div className="cs">
                <main className="hp-main cs-main cs-main--narrow">
                        <section className="hp-panel cs-state" aria-busy={busy || undefined}>
                                <h1 className="cs-state__title">{title}</h1>
                                <div className="cs-state__body">{children}</div>
                                {busy && <div className="spinner"></div>}
                        </section>
                </main>
        </div>
);

const IdHelpModal = ({ isOpen, onClose }) => {
        if (!isOpen) return null;
        return (
                <div className="cs-modal" role="dialog" aria-modal="true" aria-label="Finding a customer ID" onClick={onClose}>
                        <div className="cs-modal__card" onClick={(e) => e.stopPropagation()}>
                                <p className="cs-modal__text">
                                        In the iRacing desktop UI, click your helmet icon in the top right corner, then click <b>Your Name</b>.
                                </p>
                                <img className="cs-modal__img" src={ui_id_img} alt="Customer ID shown in the iRacing UI" />
                                <p className="cs-modal__text">
                                        <b>Or</b>, on the iRacing Member Site, open your profile page and read the Customer ID from the URL.
                                </p>
                                <img className="cs-modal__img" src={url_id_img} alt="Customer ID shown in the member site URL" />
                                <button type="button" className="cs-modal__close" onClick={onClose}>
                                        Got it
                                </button>
                        </div>
                </div>
        );
};

function CareerStats() {
        const { custid } = useParams();
        const [data, setData] = useState(null);
        const [error, setError] = useState(null);
        const [selectedCategories, setSelectedCategories] = useState(["sports_car", "formula_car", "oval", "dirt_road", "dirt_oval", "road"]);
        const [percentilesData, setPercentilesData] = useState(null);
        const [shouldRetry, setShouldRetry] = useState(false); // New state to track if we should retry
        const [isModalOpen, setModalOpen] = useState(false);
        const [cust_id, setCustId] = useState("");
        const navigate = useNavigate();
        const [consentBlocked, setConsentBlocked] = useState(false);

        const handleModalOpen = () => {
                setModalOpen(true);
        };

        const handleModalClose = () => {
                setModalOpen(false);
        };

        const handleInputChange = (event) => {
                setCustId(event.target.value);
        };

        const handleGoButtonClick = () => {
                if (cust_id.length >= 4 && !isNaN(cust_id)) {
                        navigate(`/user/careerstats/${cust_id}`);
                } else {
                        alert("Please enter a valid Customer ID");
                }
        };

        async function requestCustUpdate(cust_id) {
                try {
                        const response = await fetch(`https://iracing6-backend.herokuapp.com/api/member-career-stats/rescan/${cust_id}`, {
                                method: "POST",
                                headers: {
                                        "Content-Type": "application/json",
                                        ...authHeaders(),
                                },
                        });

                        const data = await response.json();

                        if (response.status === 200) {
                                return data;
                        } else {
                                console.error(data.error);
                                throw new Error(data.error);
                        }
                } catch (error) {
                        console.error("Error requesting cust update:", error);
                        throw error;
                }
        }

        const handleRequestUpdate = async () => {
                try {
                        const response = await requestCustUpdate(custid);
                        if (response && response.message) {
                                // Trigger the fetch again to refresh the displayed data
                                setShouldRetry(true);
                        }
                } catch (error) {
                        // Handle any errors from the requestCustUpdate function
                        alert("Error updating data: " + error.message);
                }
        };

        let lastUpdatedRelativeTime = "Loading..."; // Default text or whatever you prefer
        if (data) {
                lastUpdatedRelativeTime = moment(data.last_update).fromNow();
        }

        const retryTimeoutRef = useRef(null); // Ref to store the timeout

        // Function to check if chart data exists
        const checkAndTriggerRescan = (data) => {
                const hasData = selectedCategories.some((category) => data[category]?.iRating_chart?.data?.length);
                if (!hasData) {
                        handleRequestUpdate();
                } else {
                        setShouldRetry(true);
                }
        };

        // Fetch career stats data
        useEffect(() => {
                setConsentBlocked(false);
                if (custid) {
                        fetch(`https://iracing6-backend.herokuapp.com/api/member-career-stats/career/${custid}`, { headers: authHeaders() })
                                .then((response) => {
                                        if (response.status === 403) {
                                                const blocked = new Error("consent_required");
                                                blocked.consentRequired = true;
                                                throw blocked;
                                        }
                                        if (!response.ok) {
                                                if (response.status === 500 || response.status === 202) {
                                                        throw new Error("You have been added to the queue to be scanned.");
                                                }
                                                throw new Error("Something went wrong.");
                                        }
                                        return response.json();
                                })
                                .then((data) => {
                                        setData(data);
                                        checkAndTriggerRescan(data); // Check and trigger rescan if necessary
                                        setError(null); // Clear the error state
                                        setShouldRetry(false); // Reset shouldRetry to false
                                })
                                .catch((err) => {
                                        if (err.consentRequired) {
                                                // Driver has not opted in: show nothing about them, never retry.
                                                setData(null);
                                                setError(null);
                                                setConsentBlocked(true);
                                                return;
                                        }
                                        setError(err.message);
                                        if (err) {
                                                if (!shouldRetry) {
                                                        setShouldRetry(true);
                                                }
                                        }
                                });
                }

                return () => {
                        clearTimeout(retryTimeoutRef.current); // Clear the timeout when the component is unmounted or before starting a new fetch
                };
        }, [custid, shouldRetry, selectedCategories]);

        // Handle retry mechanism
        useEffect(() => {
                if (shouldRetry) {
                        retryTimeoutRef.current = setTimeout(() => {
                                setShouldRetry(false); // Reset shouldRetry to trigger the fetch again
                        }, 15000); // Retry after 15 seconds
                }

                return () => {
                        clearTimeout(retryTimeoutRef.current); // Clear the timeout when the component is unmounted
                };
        }, [shouldRetry]);

        // Fetch percentiles data
        useEffect(() => {
                setPercentilesData(null);
                if (!custid || consentBlocked) return;
                const controller = new AbortController();
                // Adjusted API call to fetch percentiles for all categories
                fetch(
                        `https://iracing6-backend.herokuapp.com/api/user-career-stats-percentiles/?field=wins,starts,laps,laps_led,laps_led_percentage,starts_per_win,avg_incidents&category=${selectedCategories.join(
                                ","
                        )}&cust_id=${encodeURIComponent(custid)}`, { signal: controller.signal, headers: authHeaders() }
                )
                        .then((response) => response.json())
                        .then((data) => {
                                setPercentilesData(data);
                        })
                        .catch((error) => { if (error.name !== "AbortError") console.error("Error fetching percentiles:", error); });
                return () => controller.abort();
        }, [selectedCategories, custid, data, consentBlocked]);


        // Ensure data and the specific category properties exist before rendering
        // Check if data has at least one category with valid stats
        // Exclude outdated "road" category - new accounts use sports_car/formula_car instead
        const hasValidData =
                data && ["sports_car", "formula_car", "oval", "dirt_road", "dirt_oval"].some((category) => data[category] && data[category].starts !== undefined && data[category].starts > 0);

        if (data && hasValidData) {
                // Old accounts only have the combined "road" category; show it in place of formula car.
                const ratingCategories = ["sports_car", data.formula_car || !data.road ? "formula_car" : "road", "oval", "dirt_road", "dirt_oval"];
                const chartCategories = ALL_CATEGORIES.filter((category) => category !== "road" || data.road?.iRating_chart?.data?.length);

                const traces = selectedCategories
                        .map((category) => {
                                if (data[category] && data[category].iRating_chart && Array.isArray(data[category].iRating_chart.data)) {
                                        const chartData = data[category].iRating_chart.data;
                                        return {
                                                x: chartData.map((item) => item.when),
                                                y: chartData.map((item) => item.value),
                                                type: "scatter",
                                                mode: "lines",
                                                name: CATEGORY_LABELS[category],
                                                line: { color: CATEGORY_COLORS[category], width: 2 },
                                                hovertemplate: `${CATEGORY_LABELS[category]}: %{y}<extra></extra>`,
                                        };
                                }
                                return null;
                        })
                        .filter((trace) => trace !== null);

                const statCategories = selectedCategories.filter((category) => data[category] && data[category].starts > 0);
                const isWide = window.innerWidth > 800;

                return (
                        <div className="cs">
                                <main className="hp-main cs-main">
                                        <section className="cs-header" aria-labelledby="cs-name">
                                                <div className="cs-header__who">
                                                        <h1 id="cs-name" className="cs-header__name">
                                                                {maskDriverName(data.display_name)}
                                                        </h1>
                                                        <p className="cs-header__meta">
                                                                {data.member_since && <span>Member since {moment(data.member_since).format("MMM YYYY")}</span>}
                                                                {data.last_login && <span>Last login {moment(data.last_login).fromNow()}</span>}
                                                        </p>
                                                </div>
                                                <div className="cs-header__update">
                                                        <span>Updated {lastUpdatedRelativeTime}</span>
                                                        <button type="button" className="hp-link" onClick={handleRequestUpdate}>
                                                                Request update
                                                        </button>
                                                </div>
                                        </section>

                                        <section className="cs-ratings" aria-label="iRating by category">
                                                {ratingCategories.map((category) => {
                                                        const value = data[category]?.iRating?.value;
                                                        return (
                                                                <div key={category} className="cs-rating" style={{ "--cs-cat": CATEGORY_COLORS[category] }}>
                                                                        <span className="cs-rating__label">
                                                                                {category !== "road" && <CategoryIcon category={category} size={14} />}
                                                                                {CATEGORY_LABELS[category]}
                                                                        </span>
                                                                        <span className="cs-rating__value">{Number.isFinite(value) && value > 0 ? value.toLocaleString() : "–"}</span>
                                                                </div>
                                                        );
                                                })}
                                        </section>

                                        <section className="hp-panel">
                                                <div className="hp-panel__head">
                                                        <h2 className="hp-panel__title">iRating history</h2>
                                                        <div className="hp-seg cs-toggles" role="group" aria-label="Categories shown">
                                                                {chartCategories.map((category) => {
                                                                        const active = selectedCategories.includes(category);
                                                                        const onlyOne = active && selectedCategories.length === 1;
                                                                        return (
                                                                                <button
                                                                                        key={category}
                                                                                        type="button"
                                                                                        className={`hp-seg__btn cs-toggle${active ? " is-active" : ""}`}
                                                                                        style={{ "--cs-cat": CATEGORY_COLORS[category] }}
                                                                                        aria-pressed={active}
                                                                                        disabled={onlyOne}
                                                                                        title={onlyOne ? "At least one category stays selected" : CATEGORY_LABELS[category]}
                                                                                        aria-label={CATEGORY_LABELS[category]}
                                                                                        onClick={() => handleCategoryToggle(category)}
                                                                                >
                                                                                        <span className="cs-toggle__dot" aria-hidden="true" />
                                                                                        {category === "road" ? "Road (old)" : <CategoryIcon category={category} size={16} />}
                                                                                </button>
                                                                        );
                                                                })}
                                                        </div>
                                                </div>
                                                {traces.length > 0 ? (
                                                        <div className="hp-chart">
                                                                <Plot
                                                                        data={traces}
                                                                        layout={{
                                                                                ...chartBase(),
                                                                                hovermode: "x unified",
                                                                                showlegend: false,
                                                                                xaxis: { ...chartAxis(), showgrid: false },
                                                                                yaxis: {
                                                                                        ...chartAxis(),
                                                                                        title: { text: "iRating", font: { size: 12, color: CHART.muted }, standoff: isWide ? 12 : 6 },
                                                                                        tickformat: "~s",
                                                                                },
                                                                                width: isWide ? 800 : window.innerWidth - 48,
                                                                                height: 300,
                                                                                annotations: [chartWatermark("bottom-right")],
                                                                                margin: isWide ? { l: 64, r: 16, t: 8, b: 36 } : { l: 44, r: 8, t: 8, b: 36 },
                                                                                autosize: false,
                                                                        }}
                                                                        config={{ displayModeBar: false }}
                                                                />
                                                        </div>
                                                ) : (
                                                        <p className="hp-panel__note">No iRating history for the selected categories.</p>
                                                )}
                                        </section>

                                        {statCategories.map((category) => (
                                                <section key={category} className="hp-panel cs-cat" style={{ "--cs-cat": CATEGORY_COLORS[category] }}>
                                                        <div className="hp-panel__head">
                                                                <h2 className="hp-panel__title cs-cat__title">
                                                                        {category !== "road" && <CategoryIcon category={category} size={20} />}
                                                                        {CATEGORY_LABELS[category]}
                                                                </h2>
                                                                <dl className="cs-mini">
                                                                        <div>
                                                                                <dt>Laps</dt>
                                                                                <dd>{(data[category].laps ?? 0).toLocaleString()}</dd>
                                                                        </div>
                                                                        <div>
                                                                                <dt>Poles</dt>
                                                                                <dd>{(data[category].poles ?? 0).toLocaleString()}</dd>
                                                                        </div>
                                                                        <div>
                                                                                <dt>Top 5s</dt>
                                                                                <dd>{(data[category].top5 ?? 0).toLocaleString()}</dd>
                                                                        </div>
                                                                </dl>
                                                        </div>
                                                        <div className="cs-stats">
                                                                <StatBox title="iRating starts" value={data[category].starts} percentileData={percentilesData?.[category]?.starts} />
                                                                <StatBox title="Wins" value={data[category].wins} percentileData={percentilesData?.[category]?.wins} />
                                                                <StatBox
                                                                        title="Starts per win"
                                                                        value={data[category].wins > 0 ? (data[category].starts / data[category].wins).toFixed(2) : "N/A"}
                                                                        percentileData={percentilesData?.[category]?.starts_per_win}
                                                                />
                                                                <StatBox title="Laps led" value={data[category].laps_led} percentileData={percentilesData?.[category]?.laps_led} />
                                                                <StatBox
                                                                        title="Laps led %"
                                                                        value={data[category].laps_led_percentage}
                                                                        percentileData={percentilesData?.[category]?.laps_led_percentage}
                                                                />
                                                                <StatBox
                                                                        title="Avg incidents"
                                                                        value={data[category].avg_incidents.toFixed(2)}
                                                                        percentileData={percentilesData?.[category]?.avg_incidents}
                                                                />
                                                        </div>
                                                </section>
                                        ))}
                                </main>
                        </div>
                );
        } else if (consentBlocked) {
                return (
                        <StatePanel title="This driver's profile isn't shared">
                                <p>Career stats are only shown for drivers who have opted in on iRacingData.</p>
                                <p className="cs-state__fine">
                                        If this is you, sign in with iRacing and turn on name sharing from your{" "}
                                        <button type="button" className="hp-link" onClick={() => navigate("/account")}>
                                                Account page
                                        </button>
                                        .
                                </p>
                        </StatePanel>
                );
        } else if (data && !hasValidData) {
                // Data was returned but user has no valid race stats - trigger rescan
                return (
                        <StatePanel title="This driver's stats are out of date" busy>
                                <p>They're queued for a scan, which can take up to 2 minutes.</p>
                                <p className="cs-state__fine">This page refreshes on its own.</p>
                        </StatePanel>
                );
        } else if (error === "You have been added to the queue to be scanned.") {
                return (
                        <StatePanel title="We don't have this driver yet" busy>
                                <p>They're queued for a scan, which can take up to 2 minutes.</p>
                                <p className="cs-state__fine">This page refreshes on its own.</p>
                        </StatePanel>
                );
        } else if (error) {
                return (
                        <StatePanel title="Career stats couldn't load">
                                <p>{error}</p>
                                <p className="cs-state__fine">If this keeps happening, email contact@iracingdata.com with this message.</p>
                        </StatePanel>
                );
        } else if (!custid) {
                return (
                        <div className="cs">
                                <main className="hp-main cs-main cs-main--narrow">
                                        <HomeProfileCard />
                                        <section className="hp-panel cs-search" aria-labelledby="cs-search-title">
                                                <h2 id="cs-search-title" className="hp-panel__title">
                                                        Look up a driver
                                                </h2>
                                                <p className="hp-panel__note cs-search__note">Career stats are shown for drivers who have opted in to sharing their profile.</p>
                                                <form
                                                        className="cs-search__form"
                                                        onSubmit={(e) => {
                                                                e.preventDefault();
                                                                handleGoButtonClick();
                                                        }}
                                                >
                                                        <label htmlFor="cs-cust-id" className="hp-sr-only">
                                                                iRacing customer ID
                                                        </label>
                                                        <input
                                                                id="cs-cust-id"
                                                                className="cs-search__input"
                                                                type="text"
                                                                inputMode="numeric"
                                                                autoComplete="off"
                                                                placeholder="Customer ID"
                                                                name="cust_id"
                                                                value={cust_id}
                                                                onChange={handleInputChange}
                                                        />
                                                        <button type="submit" className="cs-search__go">
                                                                View stats
                                                        </button>
                                                </form>
                                                <div className="cs-search__links">
                                                        <button type="button" className="hp-link cs-search__help" onClick={handleModalOpen}>
                                                                Where do I find a customer ID?
                                                        </button>
                                                        <button type="button" className="hp-link" onClick={() => navigate("/account")}>
                                                                Share your profile
                                                        </button>
                                                </div>
                                        </section>
                                        <IdHelpModal isOpen={isModalOpen} onClose={handleModalClose} />
                                </main>
                        </div>
                );
        } else {
                return (
                        <StatePanel title="Loading career stats" busy>
                                <p className="cs-state__fine">Fetching the latest numbers for this driver.</p>
                        </StatePanel>
                );
        }

        function handleCategoryToggle(category) {
                setSelectedCategories((prevSelectedCategories) => {
                        if (prevSelectedCategories.includes(category)) {
                                // Keep at least one category selected; the last active toggle is disabled anyway.
                                return prevSelectedCategories.length > 1 ? prevSelectedCategories.filter((cat) => cat !== category) : prevSelectedCategories;
                        }
                        return [...prevSelectedCategories, category];
                });
        }
}

export default CareerStats;
